import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { StyledProps } from "../_type";
import { createRocket } from "../_util/create-rocket";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { useConfig } from "../_util/config-context";
import { isChildOfType } from "../_util/is-child-of-type";
import { ListItem } from "./ListItem";
import { ListSubMenu } from "./ListSubMenu";
import { ListItemContent } from "./ListItemContent";
import { ListItemDecorator } from "./ListItemDecorator";
import { ListItemButton } from "./ListItemButton";
import { ListDivider } from "./ListDivider";
// import { ListGroupTitle } from "./ListGroupTitle";
import { ListSearchBox } from "./ListSearchBox";

export interface ListItemProps extends StyledProps {
  /**
   * 是否处于激活态
   */
  current?: boolean;

  /**
   * 是否处于选中态
   */
  selected?: boolean;

  /**
   * 是否处于禁用态
   */
  disabled?: boolean;

  /**
   * 点击时回调
   */
  onClick?: (evt: React.MouseEvent) => void;

  /**
   * Tooltip 说明文本
   */
  tooltip?: React.ReactNode;

  /**
   * 菜单项
   */
  children?: React.ReactNode;
}

export interface ListSubMenuProps
  extends StyledProps,
    React.LiHTMLAttributes<HTMLLIElement> {
  /**
   * 子菜单名
   */
  label?: React.ReactNode;

  /**
   * 菜单项
   */
  children?: React.ReactNode;

  /**
   * 弹出方向
   * @default "right"
   */
  placement?:
    | "right"
    | "left"
    | "right-start"
    | "right-end"
    | "left-start"
    | "left-end";

  /**
   * 展开子菜单触发方式
   * @default "hover"
   * @since 2.7.0
   */
  trigger?: "hover" | "click";

  /**
   * 是否处于禁用态
   * @since 2.5.0
   */
  disabled?: boolean;

  /**
   * 菜单项内容是否展示
   *
   * 默认为鼠标 Hover 触发，可通过该属性实现受控
   * @since 2.5.0
   */
  visible?: boolean;

  /**
   * 菜单项内容展示状态变化回调
   * @since 2.5.0
   */
  onVisibleChange?: (visible: boolean) => void;

  /**
   * 是否处于激活态
   * @since 2.6.0
   */
  current?: boolean;

  /**
   * 是否处于选中态
   * @since 2.6.0
   */
  selected?: boolean;
}

export interface ListProps extends StyledProps {
  /**
   * 列表内容。使用 `<List.Item>` 来表示列表项
   */
  children?: React.ReactNode;

  /**
   * 列表类型
   *
   * - 可以不传，表示简单平铺的列表
   * - `bullet` 列表项以点号开头
   * - `number` 列表项以列表序号开头
   * - `option` 列表以菜单的形式渲染
   * - `option-group` 列表以分组菜单的形式渲染
   * - `plain` 推荐用ItemButton来渲染List
   * - `plain-group` 推荐用ItemButton来渲染分组List //有待讨论，过于复杂

   */
  type?:
    | "bullet"
    | "number"
    | "option"
    | "plain"
    | "option-group"
    | "plain-group";

  /**
   * 列表项之间的分割方式
   *
   * - `divide` 表示使用分割线分割
   * - `stripe` 表示使用条纹背景色分割
   */
  split?: "divide" | "stripe";

  /**
   * 列表项滚动至底部的回调
   */
  onScrollBottom?: (event: React.UIEvent) => void;
}
const ListContext = React.createContext({
  type: "none",
});
export const useListContext = () => React.useContext(ListContext);

export const List = forwardRefWithStatics(
  function List(
    {
      className,
      style,
      children,
      type,
      split,
      onScrollBottom = () => null,
    }: ListProps,
    ref: React.Ref<HTMLUListElement | HTMLOListElement>
  ) {
    const { classPrefix } = useConfig();

    const [hasSubMenu, setHasSubMenu] = useState<boolean>(false);

    useEffect(() => {
      React.Children.forEach(children, child => {
        if (isChildOfType(child, ListSubMenu)) {
          setHasSubMenu(true);
        }
      });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    function handleBodyScroll(event: React.UIEvent<HTMLElement>) {
      const list = event.target as HTMLElement;
      const { scrollHeight, scrollTop, clientHeight } = list;
      if (scrollHeight <= Math.ceil(clientHeight + scrollTop + 1)) {
        onScrollBottom(event);
      }
    }

    const digits = String(React.Children.count(children)).length;

    const listClassName = classNames({
      [`${classPrefix}-list`]: true,
      [`${classPrefix}-list--bullet`]: type === "bullet",
      [`${classPrefix}-list--number`]: type === "number",
      [`${classPrefix}-list--plain`]:
        type === "plain" || type === "plain-group",
      [`${classPrefix}-list--option`]:
        type === "option" || type === "option-group",
      [`${classPrefix}-list--group`]:
        type === "option-group" || type === "plain-group",
      [`${classPrefix}-list--divider`]: split === "divide",
      [`${classPrefix}-list--striped`]: split === "stripe",
      "is-2digits": type === "number" && digits === 2,
      "is-3digits": type === "number" && digits === 3,
      "is-4digits": type === "number" && digits > 3,
      [className]: className,
    });

    const Parent = type === "number" ? "ol" : "ul";

    return (
      <Parent
        ref={ref as any}
        className={listClassName}
        style={{
          // 多级菜单展开
          // maxHeight: hasSubMenu ? "none" : undefined,
          ...(style || {}),
        }}
        onScroll={handleBodyScroll}
      >
        <ListContext.Provider
          value={{
            type,
          }}
        >
          {children}
        </ListContext.Provider>
      </Parent>
    );
  },
  {
    GroupLabel: createRocket("GroupLabel", "li.@{prefix}-list__label"),
    Divider: createRocket("Divider", "li.@{prefix}-list__separate"),
    StatusTip: createRocket("TipItem", "li.@{prefix}-list__status"),
    SubMenu: ListSubMenu,
    Item: ListItem,
    ItemButton: ListItemButton,
    ItemContent: ListItemContent,
    ItemDecorator: ListItemDecorator,
    ListDivider,
    // GroupTitle: ListGroupTitle,
    SearchBox: ListSearchBox,
  }
);

List.displayName = "List";
